import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { CopyField } from "./copy-field";
import { DocumentDropPane } from "./document-drop-pane";
import { useGuide } from "./guide-context";
import { FORWARDING_GUIDE } from "./guides";
import { InvoiceSampleList } from "./invoice-sample-list";
import { RouteHeading } from "./route-heading";
import { StepLayout } from "./step-layout";
import { StepNavigation } from "./step-navigation";
import { INVOICE_SAMPLE_SOURCE, type InvoiceSample } from "./types";
import { INVOICE_UPLOAD_TYPES } from "./upload-file-types";
import { UploadProgress } from "./upload-progress";
import { useInvoiceSampleUpload } from "./use-invoice-sample-upload";

const SEND_GUIDANCE: readonly string[] = [
  "A couple of recent invoices",
  "A mix from your usual vendors works best",
  "PDFs or photos, and a zip of them is fine",
];

const MAX_INVOICE_SAMPLES_PER_BATCH = 100;

export const InvoiceUploadScreen = ({
  businessId,
  samples,
  address,
}: {
  businessId: string;
  samples: InvoiceSample[];
  address: string | null;
}) => {
  const batch = useInvoiceSampleUpload(businessId);
  const guide = useGuide();
  // The two ways in are not a choice to make: a customer can drop files in, forward
  // them, or both. Asking which one first only delayed the part they came here to do.
  const uploaded = samples.filter(
    (sample) => sample.source === INVOICE_SAMPLE_SOURCE.upload,
  );
  const emailed = samples.filter(
    (sample) => sample.source === INVOICE_SAMPLE_SOURCE.email,
  );

  return (
    <StepLayout
      activePane="invoice-upload"
      title="Send us the invoices you already have"
      subtitle="We use them to set Volca up around how your team already works. They do not become live invoices."
      nav={
        <StepNavigation
          businessId={businessId}
          step="invoice_upload"
          disabled={batch.isUploading}
        />
      }
    >
      <Card className="w-full gap-0 py-0">
        <CardContent className="p-6">
          <Stack className="w-full" gap={2} align="stretch">
            <Text variant="title">What to send</Text>
            <ul className="ml-4 list-disc space-y-2 marker:text-subtle">
              {SEND_GUIDANCE.map((item) => (
                <li key={item}>
                  <Text tone="muted">{item}</Text>
                </li>
              ))}
            </ul>
            <Text variant="caption" tone="muted">
              Setup takes a few days after you submit your information.
            </Text>
          </Stack>
        </CardContent>
      </Card>

      <RouteHeading
        number={1}
        title="Upload them here"
        detail="Drop the files in, or pick them from your computer."
      />

      <DocumentDropPane
        maxFiles={MAX_INVOICE_SAMPLES_PER_BATCH}
        maxSize={INVOICE_UPLOAD_TYPES.fileMaxBytes}
        fileTypes={INVOICE_UPLOAD_TYPES}
        disabled={batch.isUploading}
        onFiles={(files) => void batch.upload(files)}
        hasContents={Boolean(batch.items.length || uploaded.length)}
      >
        {!!batch.items.length && <UploadProgress items={batch.items} />}
        {!batch.isUploading && !!batch.items.length && (
          <Button variant="link" onClick={() => void batch.retry()}>
            Retry failed files
          </Button>
        )}
        {/* The dashed pane already says what to do, so an empty list under it is an
            extra line saying nothing. */}
        {!!uploaded.length && <InvoiceSampleList samples={uploaded} />}
      </DocumentDropPane>

      <RouteHeading
        number={2}
        title="Or email them to Volca"
        detail="Forwarding what is already in your mail is usually quicker than finding the files."
      />

      <Card className="w-full gap-0 py-0">
        <CardContent className="p-6">
          <Stack className="w-full" gap={4} align="stretch">
            {/* The address stays on screen once invoices start arriving. It used to
                be part of the empty state, which took it away at the moment the
                customer had just proved forwarding works and wanted to send more. */}
            {address ? (
              <CopyField label="Your Volca address" value={address} />
            ) : (
              <Text tone="muted">Your Volca address is not available yet.</Text>
            )}

            {emailed.length ? (
              <InvoiceSampleList samples={emailed} />
            ) : (
              // An empty list on its own says only that nothing has happened. What
              // the customer needs here is the one action that fills it, and the
              // invoices they want are already sitting in their mail.
              <Stack className="w-full" gap={1} align="stretch">
                <Text tone="muted">
                  Nothing yet. Go through your mail and forward anything with an
                  invoice attached to the address above. Old emails are fine,
                  and you can send them in batches.
                </Text>
              </Stack>
            )}

            <Text tone="muted">
              Want them to arrive on their own?{" "}
              <button
                type="button"
                className="rounded-sm font-semibold text-link underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-hidden"
                onClick={() => guide?.setOpen(FORWARDING_GUIDE)}
              >
                Read the forwarding guide
              </button>
            </Text>
          </Stack>
        </CardContent>
      </Card>
    </StepLayout>
  );
};
